import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Minus, History, CheckCircle2, PlusCircle } from "lucide-react";

const AXES = [
  ["clarity", "Clarity"], ["usability", "Usability"], ["trust", "Trust"],
  ["value", "Value"], ["accessibility", "A11y"],
];

const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim().slice(0, 80);

// higherIsBetter: delta>0 is good (green). false => delta<0 is good.
function tone(delta, higherIsBetter = true) {
  if (!delta) return { color: "#9AA0AC", Icon: Minus };
  const good = higherIsBetter ? delta > 0 : delta < 0;
  return { color: good ? "#10B981" : "#EF4444", Icon: delta > 0 ? ArrowUpRight : ArrowDownRight };
}

function DeltaCell({ label, before, after, higherIsBetter = true, fmt = (v) => v, sub }) {
  const b = Number(before ?? 0), a = Number(after ?? 0);
  const delta = Math.round((a - b) * 100) / 100;
  const { color, Icon } = tone(delta, higherIsBetter);
  return (
    <div className="sim-card rounded-lg p-3" data-testid={`compare-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider mb-1.5">{label}</p>
      <div className="flex items-baseline gap-1.5">
        <span className="sim-mono text-[13px] text-[#9AA0AC]">{fmt(b)}</span>
        <ArrowUpRight className="w-3 h-3 text-[#71717A] rotate-45" />
        <span className="sim-head text-lg font-bold text-[#F5F5F5]">{fmt(a)}</span>
      </div>
      <div className="flex items-center gap-1 mt-0.5" style={{ color }}>
        <Icon className="w-3.5 h-3.5" />
        <span className="sim-mono text-[11px] font-semibold">
          {delta > 0 ? "+" : ""}{fmt(delta)}{sub ? ` ${sub}` : ""}
        </span>
      </div>
    </div>
  );
}

export function CompareDelta({ baseline, current, onOpenBaseline }) {
  if (!baseline || !current) return null;

  const bBacklog = baseline.remediation_backlog || [];
  const cBacklog = current.remediation_backlog || [];
  const cSet = new Set(cBacklog.map((o) => norm(o.issue)));
  const bSet = new Set(bBacklog.map((o) => norm(o.issue)));
  const resolved = bBacklog.filter((o) => !cSet.has(norm(o.issue)));
  const introduced = cBacklog.filter((o) => !bSet.has(norm(o.issue)));

  const bv = baseline.verdict_counts || {}, cv = current.verdict_counts || {};
  const pct = (v) => `${(Number(v || 0) * 100).toFixed(2)}%`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="sim-card-elevated rounded-xl p-5 border-l-2 border-l-[#00F0FF]"
      data-testid="sim-compare-delta"
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <History className="w-4 h-4 text-[#00F0FF]" />
        <span className="sim-mono text-[11px] text-[#00F0FF] uppercase tracking-wider">Before / After</span>
        <span className="text-[#9AA0AC] text-xs">vs. your previous run</span>
        {onOpenBaseline && (
          <button
            data-testid="compare-open-baseline"
            onClick={onOpenBaseline}
            className="ml-auto sim-mono text-[11px] text-[#C2C5CE] hover:text-[#00F0FF] underline underline-offset-2 decoration-[#3f3f46] hover:decoration-[#00F0FF] transition-colors"
          >
            View baseline run →
          </button>
        )}
      </div>

      {/* Headline: health score */}
      {(() => {
        const b = Number(baseline.health_score ?? 0), a = Number(current.health_score ?? 0);
        const delta = a - b;
        const { color, Icon } = tone(delta, true);
        return (
          <div className="flex items-end gap-4 mb-5" data-testid="compare-health">
            <div>
              <p className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider mb-1">Product Health</p>
              <div className="flex items-baseline gap-2">
                <span className="sim-mono text-xl text-[#9AA0AC]">{b}</span>
                <ArrowUpRight className="w-4 h-4 text-[#71717A] rotate-45" />
                <span className="sim-head text-4xl font-extrabold text-[#F5F5F5]">{a}</span>
                <span className="sim-mono text-xs text-[#9AA0AC]">/100</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 pb-1.5" style={{ color }}>
              <Icon className="w-5 h-5" />
              <span className="sim-head text-xl font-bold">{delta > 0 ? "+" : ""}{delta}</span>
              <span className="sim-mono text-[11px] uppercase">{delta > 0 ? "improved" : delta < 0 ? "regressed" : "no change"}</span>
            </div>
          </div>
        );
      })()}

      {/* Metric grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
        <DeltaCell label="Predicted CVR" before={baseline.predicted_conversion_rate} after={current.predicted_conversion_rate} fmt={pct} />
        <DeltaCell label="Would Convert" before={bv.APPROVED} after={cv.APPROVED} />
        <DeltaCell label="Hesitant" before={bv.HESITANT} after={cv.HESITANT} higherIsBetter={false} />
        <DeltaCell label="Dropped Off" before={bv.DROPPED_OFF} after={cv.DROPPED_OFF} higherIsBetter={false} />
      </div>

      {/* Radar axes */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-4">
        {AXES.map(([key, label]) => (
          <DeltaCell key={key} label={label}
            before={baseline.radar_metrics?.[key]} after={current.radar_metrics?.[key]} />
        ))}
      </div>

      {/* Remediation resolved / introduced */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="compare-remediation">
        <div className="sim-card rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
            <span className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider">Issues resolved</span>
            <span className="sim-head text-sm font-bold text-[#10B981] ml-auto" data-testid="compare-resolved-count">
              {resolved.length} / {bBacklog.length}
            </span>
          </div>
          {resolved.length > 0 ? (
            <ul className="space-y-1 mt-1.5">
              {resolved.slice(0, 4).map((o, i) => (
                <li key={i} className="text-[#D4D4D8] text-[12px] leading-snug flex gap-1.5">
                  <span className="text-[#10B981] shrink-0">✓</span><span className="line-through decoration-[#10B981]/40">{o.issue}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[#9AA0AC] text-[12px]">None of the previously flagged issues were cleared.</p>
          )}
        </div>
        <div className="sim-card rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <PlusCircle className="w-3.5 h-3.5 text-[#F59E0B]" />
            <span className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider">Newly flagged</span>
            <span className="sim-head text-sm font-bold text-[#F59E0B] ml-auto" data-testid="compare-introduced-count">
              {introduced.length}
            </span>
          </div>
          {introduced.length > 0 ? (
            <ul className="space-y-1 mt-1.5">
              {introduced.slice(0, 4).map((o, i) => (
                <li key={i} className="text-[#D4D4D8] text-[12px] leading-snug flex gap-1.5">
                  <span className="text-[#F59E0B] shrink-0">+</span><span>{o.issue}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[#9AA0AC] text-[12px]">No new issues surfaced this run.</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
